import type { InterpretationClaim } from './interpretation.js';
import { buildEvidenceIndex, resolveEvidenceRefs, type EvidenceIndexEntry } from './evidenceIndex.js';
import type { Feature } from './rules.js';

export interface ClaimExplanationFeatureEvidence {
  evidenceRef: string;
  featureId: string;
  scope: Feature['scope'];
  kind: string;
  value: Feature['value'];
  evidenceGroup: string;
}

export interface ClaimExplanationPacket {
  claimKey: string;
  conclusion: string;
  strengthLevel: InterpretationClaim['strength']['level'];
  rules: readonly {
    ruleId: string;
    sourceId: string;
    sourceTitle: string;
    sourceUrl: string | undefined;
    sourceLocator: string;
    normalizedRationale: string;
  }[];
  evidence: readonly EvidenceIndexEntry[];
  featureEvidence: readonly ClaimExplanationFeatureEvidence[];
  unresolvedEvidenceRefs: readonly string[];
  disclosure: string;
}


function resolveFeatureEvidence(claim: InterpretationClaim, features: readonly Feature[]): readonly ClaimExplanationFeatureEvidence[] {
  const wanted = new Set(claim.evidenceRefs);
  return features.flatMap((feature) => feature.evidenceRefs
    .filter((evidenceRef) => wanted.has(evidenceRef))
    .map((evidenceRef) => Object.freeze({ evidenceRef, featureId: feature.id, scope: feature.scope, kind: feature.kind, value: feature.value, evidenceGroup: feature.evidenceGroup })))
    .sort((a, b) => a.evidenceRef.localeCompare(b.evidenceRef) || a.featureId.localeCompare(b.featureId));
}

export function buildClaimExplanationPacket(
  claim: InterpretationClaim,
  features: readonly Feature[],
): ClaimExplanationPacket {
  const index = buildEvidenceIndex(features);
  const evidence = resolveEvidenceRefs(index, claim.evidenceRefs);
  const resolved = new Set(evidence.map((entry) => entry.evidenceRef));
  const unresolvedEvidenceRefs = claim.evidenceRefs.filter((ref) => !resolved.has(ref)).sort();
  return {
    claimKey: claim.claimKey,
    conclusion: claim.text,
    strengthLevel: claim.strength.level,
    rules: claim.ruleProvenance.map((entry) => ({ ...entry })),
    evidence,
    featureEvidence: resolveFeatureEvidence(claim, features),
    unresolvedEvidenceRefs,
    disclosure: '解釋鏈只說明本系統哪些傳統規則與客觀命盤 Feature 觸發了此結論，不代表科學因果或預測機率。',
  };
}
